import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import debounce from 'lodash/debounce';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit';
import type { ConnectivityPlugin } from '@atlaskit/editor-plugin-connectivity';
import { isOfflineMode } from '@atlaskit/editor-plugin-connectivity';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { SyncedBlockPlugin } from '@atlaskit/editor-plugin-synced-block';

import type { AIPlugin } from '@atlassian/editor-plugin-ai';

import { usePageRemovalState } from '@confluence/delete-pages/entry-points/usePageRemovalState.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import type { ConflictResolutionUIPlugin } from '@confluence/editor-presets/entry-points/presets/conflictResolutionUIPlugin.ts';
import { NavigationLockout } from '@confluence/navigation-lockout';
import {
	useNativeCollabState,
	useNativeCollabDraftSync,
} from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useConnected } from '@confluence/native-collab/entry-points/useConnected.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { useHasEditorContentChanged } from '@confluence/editor-features/entry-points/useHasEditorContentChanged.ts';
import { fg } from '@confluence/feature-gating';

import { useSharedAIState } from './hooks/useSharedAIState.ts';
import { useSharedSyncBlockState } from './hooks/useSharedSyncBlockState.ts';

const i18n = defineMessages({
	lockoutMessage: {
		id: 'full-page-editor.navigation-lockout-message',
		description:
			'Message warning user that navigating away from the editor may result in lost changes',
		defaultMessage: 'Changes made just now may not be saved.',
	},
});

export type EditorNavigationLockoutProps = {
	contentId: string;
	isPublishingContent: boolean;
	isFirstParticipant: boolean;
	editorPresetAPI?: PublicPluginAPI<
		[CollabEditPlugin, ConnectivityPlugin, ConflictResolutionUIPlugin, AIPlugin, SyncedBlockPlugin]
	> | null;
};

const DEBOUNCE_TIMEOUT = 1000;

/* TODO Consider refactoring this hook to return a promise that resolves when unsaved changes are no longer present, may make it easier for consumers to use.
 * TODO When refactoring this, consider that this hook is used for more than just unsaved changes so choose a better name, and:
 * - If the user has unsavedChanges we should always show the lockout.
 * - If isDraftSyncPending we should only show the lockout for the first participant… but isDraftSyncPending shouldn’t be set in the first place if the user isn’t the first participant…
 * - Do we need to check hasEditorContentChangedByAnyUser?
 */
export const useHasUnsavedChanges = ({
	contentId,
	isPublishingContent,
	isFirstParticipant,
	editorPresetAPI,
}: EditorNavigationLockoutProps): boolean => {
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
	const [{ isDraftSyncPending }] = useNativeCollabDraftSync();
	const [{ collabEditProvider, collabEditInitialized }] = useNativeCollabState();
	const [{ isPageBeingRemoved }] = usePageRemovalState();
	const { hasEditorContentChangedByAnyUser } = useHasEditorContentChanged({
		editorPresetAPI,
	});
	const { isCollabProviderConnected } = useConnected(collabEditProvider, contentId);

	/**
	 * - Draft sync is handled by first participant, so leaving should not be prevented for subsequent participants
	 * - Publishing is an exception because it handles syncing on its own before transition
	 * - The editor content has to have changed for unsaved changes to exist
	 * - Page removal is an exception because the page is being intentionally removed
	 */
	const isExceptionConditionMet =
		!isFirstParticipant ||
		isPublishingContent ||
		!hasEditorContentChangedByAnyUser ||
		(isPageBeingRemoved &&
			expValEquals('confluence_page_removal_nav_lockout_exception', 'isEnabled', true));

	const checkCollabProviderSteps = useCallback(
		({ status }) => {
			if (!collabEditProvider || !collabEditInitialized) {
				setUnsavedChanges(false);
				return;
			}
			if (status === 'attempt' || status === 'success') {
				const unconfirmedSteps = collabEditProvider.getUnconfirmedSteps();
				setUnsavedChanges(!!(unconfirmedSteps && unconfirmedSteps.length > 0));
			} else {
				// implies status === "failure"
				setUnsavedChanges(true);
			}
		},
		[collabEditProvider, collabEditInitialized],
	);

	const debouncedCheckCollabProviderSteps = useMemo(
		() => debounce(checkCollabProviderSteps, DEBOUNCE_TIMEOUT),
		[checkCollabProviderSteps],
	);

	// Collab provider commit listener
	useEffect(() => {
		if (isExceptionConditionMet || !collabEditProvider || !collabEditInitialized) {
			setUnsavedChanges(false);
			return;
		}

		collabEditProvider.on('commit-status', debouncedCheckCollabProviderSteps);
		return () => {
			collabEditProvider.off('commit-status', debouncedCheckCollabProviderSteps);
		};
	}, [
		collabEditProvider,
		collabEditInitialized,
		isExceptionConditionMet,
		debouncedCheckCollabProviderSteps,
	]);

	const [hasOfflineConflicts, setHasOfflineConflicts] = useState(false);
	usePluginStateEffect(
		editorPresetAPI,
		['collabEdit', 'connectivity', 'conflictResolutionUI'],
		({ collabEditState, connectivityState, conflictResolutionUIState }) => {
			if (
				!isCollabProviderConnected &&
				!!collabEditState?.initialised?.lastLocalOrganicBodyChangeAt
			) {
				setUnsavedChanges(!!collabEditProvider?.getUnconfirmedSteps()?.length);
			}
			if (isOfflineMode(connectivityState?.mode)) {
				setUnsavedChanges(!!collabEditProvider?.getUnconfirmedSteps()?.length);
			}
			if (expVal('platform_editor_offline_editing_web', 'isEnabled', false)) {
				const hasConflicts =
					conflictResolutionUIState?.conflicts &&
					(conflictResolutionUIState?.conflicts.inserted?.length > 0 ||
						conflictResolutionUIState?.conflicts.deleted?.length > 0);

				if (hasConflicts) {
					setHasOfflineConflicts(true);
					// This should clear the lockout state if conflicts have occured and are resolved
				} else if (hasOfflineConflicts && !hasConflicts) {
					setHasOfflineConflicts(false);
				}
			}
		},
	);

	const { aiIsStreaming } = useSharedAIState(editorPresetAPI);
	const { hasUnsavedBodiedSyncBlockChanges } = useSharedSyncBlockState(editorPresetAPI);

	/*
	 * unsavedChanges is an internal state controlled by listening to collab provider commmit events while isDraftSyncPending is a separate state controlled by draft sync hooks
	 */
	const shouldRenderLockout = unsavedChanges || isDraftSyncPending;
	return (
		(!isExceptionConditionMet && shouldRenderLockout) ||
		hasOfflineConflicts ||
		aiIsStreaming ||
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		(hasUnsavedBodiedSyncBlockChanges &&
			fg('platform_synced_block_patch_5') &&
			expValEquals('platform_synced_block', 'isEnabled', true))
	);
};

// Browser unload event behavior is also handled by the NavigationLockout component
export const EditorNavigationLockout = ({
	contentId,
	isPublishingContent,
	isFirstParticipant,
	editorPresetAPI,
}: EditorNavigationLockoutProps) => {
	const intl = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// TODO do we want this in the hook or in this component?
	useEffect(() => {
		const deferIdle = (cb) => {
			if ('requestIdleCallback' in window) {
				requestIdleCallback(cb, { timeout: 5000 });
			} else {
				requestAnimationFrame(cb);
			}
		};

		// force load of NavigationLockout on idle so it's available if the
		// connection goes offline
		deferIdle(() => {
			void NavigationLockout.preload();
		});
	}, []);

	const hasUnsavedChanges = useHasUnsavedChanges({
		contentId,
		isPublishingContent,
		isFirstParticipant,
		editorPresetAPI,
	});

	const isSeamlessTransition = (transitionCallbackIds: string[]) => {
		return fg('confluence_frontend_transition_preloading_system')
			? transitionCallbackIds.includes('live_doc_preloading_transition_callback_id')
			: false;
	};

	/**
	 * We do not want to trigger the lockout if the upcoming location is the same route with the same contentId
	 * If the above conditions are true, the user is still editing the page, but the url has changed
	 * Which implies the user is not exiting the editor, so we shouldn't block navigation.
	 */
	const isSameRoute = (currentRoute: RouteMatch, nextRoute: RouteMatch) => {
		const contentIdMatch =
			'contentId' in nextRoute.params && nextRoute.params['contentId'] === contentId;
		const routeNameMatch = currentRoute.name === nextRoute.name;

		return contentIdMatch && routeNameMatch;
	};

	const isSafeTransition = ({
		currentRoute,
		nextRoute,
		transitionCallbackIds,
	}: {
		currentRoute: RouteMatch;
		nextRoute: RouteMatch;
		transitionCallbackIds?: string[];
	}) => {
		return (
			isSameRoute(currentRoute, nextRoute) ||
			(transitionCallbackIds &&
				transitionCallbackIds.length > 0 &&
				isSeamlessTransition(transitionCallbackIds))
		);
	};

	return hasUnsavedChanges ? (
		<NavigationLockout
			message={intl.formatMessage(i18n.lockoutMessage)}
			isSafeTransition={isSafeTransition}
			onActivate={(message, currentRoute, nextRoute) => {
				// message===true tells react-router not to block the transition.
				// We only want to fire an event when a transition was blocked, so we make sure message !== true
				if (message !== true) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							actionSubject: 'navigationLockoutPrompt',
							action: 'shown',
							source: 'NavigationLockout',
							attributes: {
								nextRoute: nextRoute?.name ?? '',
								currentRoute: currentRoute?.name ?? '',
								contentId,
							},
						},
					}).fire();
				}
			}}
		/>
	) : null;
};
